/**
 * Converts a string path (e.g., "a.b[0].c") into an array of path segments,
 * splitting on dots and bracket notation. If the input is already an array,
 * it is returned as-is.
 *
 * @param path - The path to convert, as a string or an array of strings.
 * @returns An array of path segments if input is a string, or the original array if input is already an array.
 *
 * @remarks
 * This utility is useful for safely accessing deeply nested properties in objects,
 * by converting a string path into an array of keys and indices.
 */
export function stringToPath(path: string | string[]): string[] {
	if (Array.isArray(path)) return path;
	if (!path) return [];
	const result: string[] = [];
	const regex = /([^[.\]]+)|\[(?:(\d+)|(["'])(.*?)\3|([^[\]]+))\]/g;
	let match: RegExpExecArray | null;
	while ((match = regex.exec(path))) {
		if (match[1]) {
			result.push(match[1]);
		} else if (match[2]) {
			result.push(match[2]);
		} else if (match[4]) {
			result.push(match[4]);
		} else if (match[5]) {
			result.push(match[5]);
		}
	}
	return result;
}

/**
 * Retrieves the value at a given path within a nested object.
 *
 * The path can be provided as a string (e.g., "a.b.c") or as an array of strings.
 * If the path does not exist in the object, the function returns the provided default value (`def`), or `null` if not specified.
 *
 * @param obj - The object to traverse.
 * @param path - The path to the desired property, as a string or array of strings.
 * @param def - The default value to return if the path does not exist. Defaults to `null`.
 * @returns The value at the specified path, or the default value if the path is not found.
 */
export function getObjectByPath(obj: any, path: string | string[], defaultValue: any = null): any {
	const keys = stringToPath(path);
	let result = obj;
	for (const key of keys) {
		if (result == null || !(key in result)) {
			return defaultValue;
		}
		result = result[key];
	}
	return result === undefined ? defaultValue : result;
}
